import { CountryConfig } from 'src/lib/country.utils'

export const getCountryCode = (phoneNumber) => {
  if (!phoneNumber) return null
  // Regular expression to match the country code part of the phone number
  const countryCodeRegex = /^\+1|^\+(\d{2})/
  const match = phoneNumber.match(countryCodeRegex)

  if (match) {
    return match[0] === CountryConfig.US.phoneCode
      ? CountryConfig.US.phoneCode
      : `+${match[1]}`
  } else {
    return null
  }
}

export const removeCountryCode = (phoneNumber) => {
  if (!phoneNumber) return phoneNumber
  // Regular expression to match the country code part of the phone number
  const countryCodeRegex = /^\+1|^\+(\d{2})/
  const match = phoneNumber.match(countryCodeRegex)

  if (match) {
    return phoneNumber.replace(match[0], '')
  } else {
    return phoneNumber
  }
}

// Example: (555) 555 - 5555 -> 5555555555
/** Last 10 digits, so "(315) 238-8901", "13152388901" and "+13152388901" agree. */
export const usDigits = (value?: string | null): string =>
  (value ?? '').replace(/\D/g, '').slice(-10)

export const unformatPhoneNumber = (value) => {
  if (!value) return value
  return value.replace(/[^\d+]/g, '')
}

// Example: 5555555555 -> 55-5555-5555 (no form)
export const formatMexicanPhoneNumberString = (value) => {
  if (!value) return value
  let numbers = value.replace(/\D/g, '')
  if (numbers.length > 10) {
    numbers = numbers.slice(0, 10)
  }
  const char = { 2: '-', 6: '-' }
  let formattedValue = ''
  for (let i = 0; i < numbers.length; i++) {
    formattedValue += (char[i] || '') + numbers[i]
  }
  return formattedValue
}

export const formatPhoneNumberString = (value) => {
  if (!value) return value
  let numbers = value.replace(/\D/g, '')
  if (numbers.length > 10) {
    numbers = numbers.slice(0, 10)
  }
  const char = { 0: '(', 3: ') ', 6: ' - ' }
  let formattedValue = ''
  for (let i = 0; i < numbers.length; i++) {
    formattedValue += (char[i] || '') + numbers[i]
  }
  return formattedValue
}

export const formatPhone = (phone) => {
  if (!phone) return phone
  const countryCode = getCountryCode(phone)
  const phoneNumber = removeCountryCode(phone)

  if (countryCode === CountryConfig.MX.phoneCode) {
    return formatMexicanPhoneNumberString(phoneNumber)
  } else {
    return formatPhoneNumberString(phoneNumber)
  }
}

/**
 * A phone number as a screen should read it, or null when there is none.
 *
 * Falls back to the stored value rather than to nothing when the number cannot
 * be parsed: formatInternationalPhoneNumber returns undefined for an extension
 * or a line outside the US and MX, and reporting a field as empty when it holds
 * a real number is a worse answer than showing it unformatted.
 */
export const displayPhoneNumber = (
  phoneNumber: string | null | undefined
): string | null => {
  const trimmed = phoneNumber?.trim()
  if (!trimmed) return null
  return formatInternationalPhoneNumber(trimmed) ?? trimmed
}

export const formatInternationalPhoneNumber = (
  phoneNumber: string | null | undefined
) => {
  if (!phoneNumber) return undefined

  // Remove all non-digit characters except the plus sign
  phoneNumber = phoneNumber.replace(/[^\d+]/g, '')

  if (!phoneNumber || phoneNumber === '+') return undefined

  // Check for American or Mexican country codes
  if (phoneNumber.startsWith(CountryConfig.US.phoneCode)) {
    // Format American phone number
    const match = phoneNumber.match(/^(\+1)(\d{3})(\d{3})(\d{4})$/)
    if (match) {
      return `${match[1]} (${match[2]}) ${match[3]}-${match[4]}`
    }
    return undefined
  } else if (phoneNumber.startsWith(CountryConfig.MX.phoneCode)) {
    // Format Mexican phone number
    const match = phoneNumber.match(/^(\+52)(\d{2})(\d{4})(\d{4})$/)
    if (match) {
      return `${match[1]} ${match[2]}-${match[3]}-${match[4]}`
    }
    return undefined
  } else {
    // Assume the phone number is American and add the country code
    const match = phoneNumber.match(/^(\d{3})(\d{3})(\d{4})$/)
    if (match) {
      return `+1 (${match[1]}) ${match[2]}-${match[3]}`
    }
    return undefined
  }
}

// Build a phone-input value from a URL `?phone=` param. Only +1 (US) and +52
// (MX) are supported: a recognized code is honored, bare digits default to US,
// and any other +code isn't a number we produce — so skip it rather than
// render a bogus country in the picker. Returns '' when there's nothing usable.
export const prefillPhoneFromParam = (raw?: string): string => {
  if (typeof raw !== 'string' || !raw.trim()) return ''
  const trimmed = raw.trim()
  const detected = trimmed.startsWith('+') ? getCountryCode(trimmed) : null
  const isSupported =
    detected === CountryConfig.US.phoneCode ||
    detected === CountryConfig.MX.phoneCode
  if (trimmed.startsWith('+') && !isSupported) return ''
  const countryCode = isSupported ? detected : CountryConfig.US.phoneCode
  const national = (isSupported ? removeCountryCode(trimmed) : trimmed).replace(
    /\D/g,
    ''
  )
  return national ? `${countryCode}${national}` : ''
}

/**
 * Whether free-form user input looks like a US phone number,
 * based on having at least 10 digits after removing punctuation/spaces.
 * Mirrors server-side heuristics in api/src/lib/utils/contact.utils.ts
 * for client-side gating (without enforcing full NANP rules).
 */
export const isPhoneLikeInput = (raw?: string | null): boolean => {
  if (!raw) return false
  return usDigits(raw).length === 10
}
